import React, { useMemo, useEffect } from "react";
import type {
    PersonalisationItemType,
    PersonalizationType,
} from "../../../../../../common-deprecated/types/CommonTypes";
import useSlider from "../../../../../../common-deprecated/hooks/useSlider";
import * as Styles from "./styles/ItemSlider";
import { isLightColour } from "../../../../../../common-deprecated/utils";
import { isItemFirstOfType } from "../../../utils/sliderUtils";
import useItemSlider from "../../../../../../common-deprecated/hooks/useItemSlider";
import Icon from "../../../../../../common-deprecated/styles/v2/toyota/globals/Icon";
import SrOnly from "../../../../../../common-deprecated/styles/v2/toyota/utilities/ScreenreaderOnly";

type ItemPropsType<T> = {
    item: T;
    isSelected: boolean;
    onClick: () => void;
    showDivider: boolean;
    activeItemType: PersonalizationType;
};
const Item = <T extends { name?: string; image: string; rgb?: string }>(props: ItemPropsType<T>): JSX.Element => {
    const { item, isSelected, onClick, showDivider, activeItemType } = props;
    const lightColour = !!item.rgb && isLightColour(item.rgb);
    return (
        <Styles.ItemWrapper onClick={onClick} showDivider={showDivider}>
            <Styles.ExteriorImage
                image={item.image}
                rgb={item.rgb}
                isSelected={isSelected}
                zoomedIn={activeItemType === "colour"}
                lightColour={lightColour}
            />
            <SrOnly>{item.name}</SrOnly>
        </Styles.ItemWrapper>
    );
};

export type ItemSliderPropsType = {
    items: PersonalisationItemType[];
    activeItem?: PersonalisationItemType | null;
    activeItemType: PersonalizationType;
    selectItem: (item: PersonalisationItemType) => void;
};

const ItemSlider = ({ items, activeItem, activeItemType, selectItem }: ItemSliderPropsType): JSX.Element => {
    const { itemWidth, viewportWidth, listWrapper, isRTL } = useItemSlider(items, Styles.TOTAL_WIDTH);

    // Retrieve slider props.
    const sliderProps = useSlider(items.length * itemWidth, viewportWidth, itemWidth);
    const { enableCssTransition, rightArrowEnabled, leftArrowEnabled, rightArrowClickPage, leftArrowClickPage } =
        sliderProps;
    const { startTouchDrag, startMouseDrag, enableDrag, sliderX } = sliderProps;
    const { isScrolling, totalSliderWidth, resetSlider, onTransitionEnd } = sliderProps;

    const renderedItems = useMemo(
        () => (
            <>
                {items.map((item, index) => {
                    return (
                        <Item
                            key={item.id}
                            showDivider={index > 0 && isItemFirstOfType(items, index)}
                            item={item}
                            isSelected={!!activeItem && item.id === activeItem.id}
                            activeItemType={activeItemType}
                            onClick={() => {
                                if (!isScrolling.current) {
                                    selectItem(item);
                                }
                            }}
                        />
                    );
                })}
            </>
        ),
        // OR-9070: We added items.length because array gets re-assigned every time and this prevents pointless re-renders. However if the case ever occurs where a bug is reported that the items don't update when the length remains, please modify as follows:
        // declare const itemIds = items.map({id} => id).join() outside of this useMemo but in the component scope
        // replace items.length with the itemIds
        // alternatives are possible but that seems efficient to me
        [activeItem, activeItemType, sliderX, items.length],
    );

    // Reset slider position on tab change
    useEffect(() => {
        resetSlider();
    }, [activeItemType]);

    // (Based on Slider implementation of grade-explorer).
    return (
        <Styles.Wrapper>
            <Styles.SliderButton show={leftArrowEnabled} alignment="left" onClick={leftArrowClickPage}>
                <Icon variant="chevronLeft" />
            </Styles.SliderButton>
            <Styles.Slider>
                <Styles.SliderViewport ref={listWrapper} rightArrowVisible={rightArrowEnabled}>
                    <Styles.Track
                        // We need set the left/right style directly here because using styled components causes performance issues
                        // TODO Performance upgrade: use transform + translateX props in styled component attrs (also needs isRTL logic in hook)
                        style={isRTL ? { right: -sliderX } : { left: sliderX }}
                        enableTransition={enableCssTransition}
                        width={totalSliderWidth}
                        onTransitionEnd={onTransitionEnd}
                        onTouchStart={enableDrag ? startTouchDrag : undefined}
                        onMouseDown={enableDrag ? startMouseDrag : undefined}
                        onClick={(event) => {
                            // Prevent the click from working when the slider is scrolling.
                            if (isScrolling.current) {
                                event.preventDefault();
                                event.stopPropagation();
                            }
                        }}
                    >
                        {renderedItems}
                    </Styles.Track>
                </Styles.SliderViewport>
            </Styles.Slider>
            <Styles.SliderButton show={rightArrowEnabled} alignment="right" onClick={rightArrowClickPage}>
                <Icon variant="chevronRight" />
            </Styles.SliderButton>
        </Styles.Wrapper>
    );
};

export default ItemSlider;
